import { useEffect, useState } from 'react';
import { isValidPhoneNumber } from 'react-phone-number-input';

import { useQueryParams } from '@innovamat/hooks';
import {
  getRegisterLanguagesByRegion,
  isValidRegion,
  rootRegionToRegion,
} from '@innovamat/localization';
import { getStandardByRegion, validators } from '@innovamat/radiance-utils';

import { useTranslation } from 'react-i18next';
import { useUser } from '../../../../user-management';
import { useRegisterLead } from '../../../hooks/use-register-lead';
import type { LeadUser } from '../../../hooks/use-register-lead/register-lead';
import { useGetUserRegion } from '../../../hooks/use-user-region';
import { useLanguageFromUrl } from './use-language-from-url';

type UseLeadRegisterReturn = {
  user: LeadUser;
  phoneDialCode: string;
  languages: string[];
  region: string;
  langOptions: Array<{ value: string; label: string }>;
  isPending: boolean;
  isSuccess: boolean;
  isError: boolean;
  error: any;
  changeInput: (value: string, key: string, subkey?: string) => void;
  handleChangePhone: (value: string, country: any) => void;
  handleRegisterUser: (e: React.FormEvent) => void;
  validateInputs: () => boolean;
  getError: () => string;
};

export function useLeadRegister(): UseLeadRegisterReturn {
  const { t } = useTranslation();
  const { isLoggedIn, onClearUserSession } = useUser();
  const { language } = useLanguageFromUrl();
  const { mutate, isPending, isSuccess, isError, error } = useRegisterLead();

  const query = useQueryParams();
  const urlRegion = useGetUserRegion();

  const crmId = query.get('id');
  const standardParam = query.get('standard');
  const trialExpirationDaysParam = query.get('tr');
  const utm_source = query.get('utm_source');
  const utm_medium = query.get('utm_medium');
  const utm_campaign = query.get('utm_campaign');

  const region = rootRegionToRegion(urlRegion);
  const languages = getRegisterLanguagesByRegion(region);

  const [user, setUser] = useState<LeadUser>({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    preferences: {
      language: language || '',
      standardStructure: standardParam || '',
      ...(trialExpirationDaysParam && {
        trialExpirationDays: Number(trialExpirationDaysParam),
      }),
    },
    crmId,
  });
  const [phoneDialCode, setPhoneDialCode] = useState('');

  const { firstName, lastName, email, phoneNumber, preferences } = user;

  const getStandard = () => {
    if (standardParam) {
      return standardParam;
    }
    return getStandardByRegion(region);
  };

  const handleRegisterUser = (e: React.FormEvent) => {
    e.preventDefault();

    const userToRegister = {
      ...user,
      region,
      preferences: {
        ...user.preferences,
        standardStructure: getStandard(),
      },
    };

    if (language && !languages.includes(language)) {
      userToRegister.preferences.language = languages[0];
    }

    if (
      !userToRegister.phoneNumber ||
      !isValidPhoneNumber(userToRegister.phoneNumber)
    ) {
      userToRegister.phoneNumber = null;
    }

    if (!validateInputs()) {
      return;
    }

    const utmInfo = {
      utm_source,
      utm_medium,
      utm_campaign,
    };

    mutate({ user: userToRegister, utmInfo });
  };

  useEffect(() => {
    if (isLoggedIn) {
      onClearUserSession();
    }
  }, [isLoggedIn, onClearUserSession]);

  const getError = () => {
    if (error?.response?.data?.type === 'email.invalid') {
      return t('teachers.inviteModal.error.invalidEmail', { email });
    }

    switch (error?.response?.status) {
      case 410:
        return t(
          'Ha ocurrido un error al completar el registro. Ponte en contacto con tu asesor.'
        );
      case 406:
        return t(
          'Para poder crear un usuario del Gestor de Aula ponte en contacto con Innovamat llamando al (+34) 93 684 58 87'
        );
      default:
        return t(
          'Ha ocurrido un error al completar el registro. Inténtalo de nuevo.'
        );
    }
  };

  const changeInput = (value: string, key: string, subkey?: string) => {
    if (!subkey) {
      setUser({ ...user, [key]: value });
    } else {
      setUser((user: any) => ({
        ...user,
        [key]: {
          ...user[key],
          [subkey]: value,
        },
      }));
    }
  };

  const validateInputs = () =>
    !!firstName.trim() &&
    !!lastName.trim() &&
    validators.isEmailValid(email) &&
    isLangValid() &&
    isValidRegion(region) &&
    validators.isPhoneValid(phoneNumber, phoneDialCode);

  const handleChangePhone = (value: string, country: any) => {
    if (country.dialCode !== phoneDialCode) {
      setPhoneDialCode(country.dialCode);
    }
    setUser((prevState) => ({
      ...prevState,
      phoneNumber: `+${value}`,
    }));
  };

  const isLangValid = () => {
    if (!languages) {
      return false;
    }

    return Object.values(languages)
      .flatMap((lang) => lang)
      .some((lang) => lang === preferences.language);
  };

  const langOptions = languages?.map((lang) => ({
    value: lang,
    label: t(`language.${lang}`),
  }));

  return {
    user,
    phoneDialCode,
    languages,
    region,
    langOptions,
    isPending,
    isSuccess,
    isError,
    error,
    changeInput,
    handleChangePhone,
    handleRegisterUser,
    validateInputs,
    getError,
  };
}
